const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://aptirix-version-610696728606.asia-south1.run.app/api';

// Fallback API key for testing
const FALLBACK_API_KEY = 'course-provider-test-key-2024';

async function request(endpoint, options = {}) {
  const apiKey = localStorage.getItem('course_provider_api_key') || FALLBACK_API_KEY;
  
  const headers = {
    'Content-Type': 'application/json',
    'X-API-Key': apiKey,
    ...(options.headers || {}),
  };

  console.log(`🔍 Slides API request: ${endpoint}`, { apiKey: apiKey.substring(0, 10) + '...' });

  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    ...options,
    headers,
  });

  const data = await response.json().catch(() => ({}));

  if (!response.ok) {
    const message = data?.detail || 'Something went wrong. Please try again.';
    console.error(`❌ Slides API error: ${message}`);
    throw new Error(message);
  }

  console.log(`✅ Slides API success: ${endpoint}`);
  return data;
}

export const getTemplates = async () => {
  return request('/slides/templates');
};

export const createSlide = async (slideData) => {
  return request('/slides', {
    method: 'POST',
    body: JSON.stringify(slideData),
  });
};

export const getSlides = async (courseId, moduleId, topicId) => {
  return request(`/slides/course/${courseId}/module/${moduleId}/topic/${topicId}`);
};

export const updateSlide = async (slideId, updateData) => {
  return request(`/slides/${slideId}`, {
    method: 'PUT',
    body: JSON.stringify(updateData),
  });
};

export const deleteSlide = async (slideId) => {
  return request(`/slides/${slideId}`, {
    method: 'DELETE',
  });
};

export const publishSlides = async (courseId, moduleId, topicId) => {
  return request(`/slides/course/${courseId}/module/${moduleId}/topic/${topicId}/publish`, {
    method: 'POST',
  });
};

export const generateSlidesWithAI = async (generationRequest) => {
  return request('/slides/generate', {
    method: 'POST',
    body: JSON.stringify(generationRequest),
  });
};

export const reorderSlides = async (courseId, moduleId, topicId, slideIds) => {
  return request(`/slides/course/${courseId}/module/${moduleId}/topic/${topicId}/reorder`, {
    method: 'POST',
    body: JSON.stringify({ slide_ids: slideIds }),
  });
};

// Get all slides for a module (across all topics) - used by live teaching
export const getSlidesForModule = async (courseId, moduleId) => {
  // Endpoint requires JWT (student session)
  let session = null;
  try {
    const SessionManager = (await import('../utils/sessionManager')).default;
    session = SessionManager.getCurrentSession() || SessionManager.getSession('candidate');
  } catch (e) { /* ignore */ }
  const token = session?.access_token;
  const headers = token ? { Authorization: `Bearer ${token}` } : {};
  const response = await fetch(`${API_BASE_URL}/slides/course/${courseId}/module/${moduleId}`, { headers });
  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data?.detail || 'Failed to fetch module slides');
  }
  return response.json();
};

// Upload a .pptx file for a course module - extracts and stores slides
export const uploadPPTForModule = async (courseId, moduleId, file, providerId, topicId = 'ppt-all') => {
  const formData = new FormData();
  formData.append('file', file);

  const url = `${API_BASE_URL}/course-provider/courses/${courseId}/modules/${moduleId}/upload-ppt?provider_id=${providerId}&topic_id=${topicId}`;

  const response = await fetch(url, {
    method: 'POST',
    body: formData, // Browser sets multipart Content-Type with boundary
  });

  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(data?.detail || 'PPT upload failed');
  }
  return data;
};
